import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { HydratedPost, VoiceParticipant } from '../types';
import { PostCard } from './PostCard';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { UsersIcon } from './Icons';

interface FeaturedPostsProps {
    posts: HydratedPost[];
    onPostClick: (post: HydratedPost) => void;
    onLike: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number, post?: HydratedPost) => void;
    onNavigateToProfile: (userId: string) => void;
    onNavigateToHashtag: (tag: string) => void;
    onNavigateToCommunity?: (communityId: string, channelId?: string) => void;
    onShare?: (post: HydratedPost, slideIndex?: number) => void;
    onVouch?: (postId: number) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    onReportPost?: (postId: number) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    galleryAnimation: string;
    postBlurOpacity: number;
    isModalOpen?: boolean;
    onAddToCart?: (post: any) => void;
    isShakingMode?: boolean;
    postcardGap?: number;
    voiceRooms?: Record<string, VoiceParticipant[]>;
    communities?: any[];
}

interface ActiveLiveStream {
    username: string;
    communityId: string;
    channelId: string;
    communityName: string;
    communityAvatar?: string;
    channelName?: string;
}

// Fisher-Yates shuffle helper
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

export const FeaturedPosts: React.FC<FeaturedPostsProps> = ({
    posts,
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onNavigateToProfile,
    onNavigateToHashtag,
    onNavigateToCommunity,
    onShare,
    onVouch,
    onMuteUser,
    onBlockUser,
    onGrantAdmin,
    onDeleteUser,
    onReportPost,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    galleryAnimation,
    postBlurOpacity,
    isModalOpen,
    onAddToCart,
    isShakingMode,
    postcardGap,
    voiceRooms = {},
    communities = []
}) => {
    const { currentUser, usersData } = useAuth() || {};
    const currentUserData = currentUser ? usersData?.[currentUser] : null;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';

    // Active live screen streams in public communities
    const activeLiveStreams = useMemo<ActiveLiveStream[]>(() => {
        if (!voiceRooms || !communities || communities.length === 0) return [];
        const list: ActiveLiveStream[] = [];

        Object.entries(voiceRooms).forEach(([roomKey, participants]) => {
            if (!Array.isArray(participants) || participants.length === 0) return;

            const parts = roomKey.split(':');
            if (parts.length < 2) return;
            const communityId = parts[0];
            const channelId = parts[1];

            // Match public community
            const comm = communities.find((c: any) => String(c._id) === String(communityId) || String(c.id) === String(communityId));
            if (!comm) return;
            if (comm.isPrivate || comm.visibility === 'private') return;

            participants.forEach(p => {
                if (p.isScreenSharing) {
                    const chan = (comm.channels || []).find((ch: any) => String(ch.id) === String(channelId) || String(ch._id) === String(channelId));
                    list.push({
                        username: p.username,
                        communityId,
                        channelId,
                        communityName: comm.name,
                        communityAvatar: comm.avatar,
                        channelName: chan?.name || 'voice'
                    });
                }
            });
        });

        return list;
    }, [voiceRooms, communities]);

    const storageKey = `featured_posts_filter_${currentUser || 'default'}`;
    const [filterMode, setFilterMode] = useState<'all' | 'connections' | 'live'>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved === 'all' || saved === 'connections' || saved === 'live') return saved as any;
        } catch {}
        return 'all';
    });

    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved === 'all' || saved === 'connections' || saved === 'live') {
                setFilterMode(saved as any);
            }
        } catch {}
    }, [storageKey]);

    // Fallback if live filter is active but no active lives remain
    useEffect(() => {
        if (filterMode === 'live' && activeLiveStreams.length === 0) {
            setFilterMode('all');
        }
    }, [filterMode, activeLiveStreams.length]);

    const handleFilterChange = (mode: 'all' | 'connections' | 'live') => {
        setFilterMode(mode);
        try {
            localStorage.setItem(storageKey, mode);
        } catch {}
    };

    const [currentIndex, setCurrentIndex] = useState(0);

    // Build connections set: friends, followers, following + currentUser
    const connectionsSet = useMemo(() => {
        if (!currentUserData && !currentUser) return new Set<string>();
        const friends = currentUserData?.friends || [];
        const followers = currentUserData?.followers || [];
        const following = currentUserData?.following || [];
        const self = currentUser ? [currentUser] : [];
        return new Set<string>([...friends, ...followers, ...following, ...self]);
    }, [currentUserData, currentUser]);

    // Filter base public posts
    const eligiblePublicPosts = useMemo(() => {
        return posts.filter(p => {
            if (p.isArchived) return false;
            if (p.communityId) return false; // Exclude community-only posts
            if (p.visibility !== 'public') return false; // Strictly public posts

            if (filterMode === 'connections') {
                return connectionsSet.has(p.author);
            }
            return true;
        });
    }, [posts, filterMode, connectionsSet]);

    // Maintain a shuffled list of eligible posts
    const [shuffledPosts, setShuffledPosts] = useState<HydratedPost[]>([]);

    useEffect(() => {
        if (filterMode === 'live') {
            setCurrentIndex(0);
        } else if (eligiblePublicPosts.length > 0) {
            setShuffledPosts(shuffleArray(eligiblePublicPosts));
            setCurrentIndex(0);
        } else {
            setShuffledPosts([]);
            setCurrentIndex(0);
        }
    }, [eligiblePublicPosts, filterMode]);

    // Timer ref for 8-second cycling
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const activeListLength = filterMode === 'live' ? activeLiveStreams.length : shuffledPosts.length;

    const resetTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        if (activeListLength > 1) {
            timerRef.current = setInterval(() => {
                setCurrentIndex(prev => (prev + 1) % activeListLength);
            }, 8000);
        }
    }, [activeListLength]);

    useEffect(() => {
        resetTimer();
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [resetTimer]);

    const handleNext = () => {
        if (activeListLength === 0) return;
        setCurrentIndex(prev => (prev + 1) % activeListLength);
        resetTimer();
    };

    const handlePrev = () => {
        if (activeListLength === 0) return;
        setCurrentIndex(prev => (prev - 1 + activeListLength) % activeListLength);
        resetTimer();
    };

    const activePost = filterMode !== 'live' ? (shuffledPosts[currentIndex] || null) : null;
    const activeLiveStream = filterMode === 'live' ? (activeLiveStreams[currentIndex] || null) : null;

    return (
        <div className="w-full my-[2vh] flex flex-col items-center px-[2vw]">
            {/* Header & Filter Pill Controls */}
            <div className="w-full max-w-[650px] flex items-center justify-between mb-[1.5vh]">
                <div 
                    className="text-[0.95rem] font-bold tracking-wide flex items-center gap-[0.5vw]"
                    style={{ color: userThemeColor, ...stickerTextStyle(0.18) }}
                >
                    <span className="text-[1.1rem]">✨</span> Featured Posts
                </div>

                <div className="flex items-center bg-black/30 backdrop-blur-md p-[0.3vh] rounded-full border border-white/10 text-[0.8rem]">
                    {/* Live Tab with Pulsating Dot */}
                    {activeLiveStreams.length > 0 && (
                        <button
                            onClick={() => handleFilterChange('live')}
                            className={`px-[1.2vw] py-[0.5vh] rounded-full transition-all duration-200 font-bold flex items-center gap-1.5 ${
                                filterMode === 'live' 
                                    ? 'shadow-md text-white bg-red-600' 
                                    : 'text-red-400 hover:text-white'
                            }`}
                        >
                            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                            LIVE
                        </button>
                    )}
                    <button
                        onClick={() => handleFilterChange('all')}
                        className={`px-[1.2vw] py-[0.5vh] rounded-full transition-all duration-200 font-semibold ${
                            filterMode === 'all' 
                                ? 'shadow-md text-white' 
                                : 'text-gray-400 hover:text-white'
                        }`}
                        style={filterMode === 'all' ? { backgroundColor: userThemeColor } : {}}
                    >
                        All Posts
                    </button>
                    <button
                        onClick={() => handleFilterChange('connections')}
                        className={`px-[1.2vw] py-[0.5vh] rounded-full transition-all duration-200 font-semibold ${
                            filterMode === 'connections' 
                                ? 'shadow-md text-white' 
                                : 'text-gray-400 hover:text-white'
                        }`}
                        style={filterMode === 'connections' ? { backgroundColor: userThemeColor } : {}}
                    >
                        Connections Only
                    </button>
                </div>
            </div>

            {/* LIVE Stream Feed Postcard View */}
            {filterMode === 'live' && activeLiveStream ? (
                <div className="w-full max-w-[700px] flex items-center justify-between gap-[1vw]">
                    {/* Left Arrow Button */}
                    <button
                        onClick={handlePrev}
                        disabled={activeLiveStreams.length <= 1}
                        className="p-[1.2vh] rounded-full bg-black/40 hover:bg-black/70 border border-white/10 backdrop-blur-md text-white transition-all transform hover:scale-105 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                        title="Previous Live Stream"
                        aria-label="Previous Live Stream"
                    >
                        <svg className="w-[2.2vh] h-[2.2vh]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Live Screen Stream Postcard */}
                    <div 
                        onClick={() => onNavigateToCommunity?.(activeLiveStream.communityId, activeLiveStream.channelId)}
                        className="flex-1 w-full rounded-[2.5rem] bg-black/40 border border-red-500/40 backdrop-blur-md p-[2vh] flex flex-col gap-[1.5vh] cursor-pointer hover:border-red-500/70 transition-all shadow-2xl group relative overflow-hidden"
                    >
                        {/* Header: Streamer Info & Community Badge */}
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-3">
                                <div className="relative">
                                    <img 
                                        src={usersData?.[activeLiveStream.username]?.pfp || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(activeLiveStream.username)} 
                                        alt={activeLiveStream.username}
                                        className="w-10 h-10 rounded-full object-cover border-2 border-red-500"
                                    />
                                    <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-black animate-pulse" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="font-bold text-white text-[0.95rem] flex items-center gap-1.5" style={stickerTextStyle(0.18)}>
                                        {activeLiveStream.username}
                                    </span>
                                    <span className="text-[0.75rem] text-red-400 font-bold flex items-center gap-1">
                                        🔴 LIVE SCREEN SHARE
                                    </span>
                                </div>
                            </div>

                            {/* Community Badge */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onNavigateToCommunity?.(activeLiveStream.communityId, activeLiveStream.channelId);
                                }}
                                className="flex items-center gap-1.5 bg-black/50 rounded-full py-1.5 px-3 text-[11px] font-bold text-white hover:bg-black/80 transition-all duration-300 border border-white/10 shadow-sm flex-shrink-0"
                            >
                                {activeLiveStream.communityAvatar ? (
                                    <img src={activeLiveStream.communityAvatar} alt={activeLiveStream.communityName} className="w-4 h-4 rounded-full object-cover flex-shrink-0" />
                                ) : (
                                    <UsersIcon className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                                )}
                                <span className="truncate max-w-[120px]">{activeLiveStream.communityName}</span>
                                {activeLiveStream.channelName && (
                                    <span className="text-gray-400 font-normal">#{activeLiveStream.channelName}</span>
                                )}
                            </button>
                        </div>

                        {/* Stream Preview Media Box */}
                        <div className="w-full aspect-video rounded-2xl bg-gradient-to-br from-gray-950 via-black to-red-950/30 border border-white/10 flex flex-col items-center justify-center p-4 relative overflow-hidden group-hover:border-red-500/50 transition-colors">
                            <div className="absolute inset-0 bg-red-500/5 animate-pulse pointer-events-none" />
                            <div className="z-10 flex flex-col items-center gap-2 text-center">
                                <div className="w-14 h-14 rounded-full bg-red-500/20 border border-red-500/50 flex items-center justify-center text-red-400 text-2xl animate-bounce shadow-lg">
                                    📺
                                </div>
                                <span className="text-white font-bold text-[1.05rem]" style={stickerTextStyle(0.18)}>
                                    Join Screen Share in #{activeLiveStream.channelName}
                                </span>
                                <span className="text-xs text-gray-400 font-medium">
                                    Click anywhere to jump into {activeLiveStream.communityName}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Right Arrow Button */}
                    <button
                        onClick={handleNext}
                        disabled={activeLiveStreams.length <= 1}
                        className="p-[1.2vh] rounded-full bg-black/40 hover:bg-black/70 border border-white/10 backdrop-blur-md text-white transition-all transform hover:scale-105 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                        title="Next Live Stream"
                        aria-label="Next Live Stream"
                    >
                        <svg className="w-[2.2vh] h-[2.2vh]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            ) : activePost ? (
                /* Post Carousel Container with Arrow Controls */
                <div className="w-full max-w-[700px] flex items-center justify-between gap-[1vw]">
                    {/* Left Arrow Button */}
                    <button
                        onClick={handlePrev}
                        disabled={shuffledPosts.length <= 1}
                        className="p-[1.2vh] rounded-full bg-black/40 hover:bg-black/70 border border-white/10 backdrop-blur-md text-white transition-all transform hover:scale-105 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                        title="Previous Post"
                        aria-label="Previous Featured Post"
                    >
                        <svg className="w-[2.2vh] h-[2.2vh]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Post Card Container */}
                    <div className="flex-1 w-full overflow-hidden">
                        <PostCard
                            post={activePost}
                            onPostClick={onPostClick}
                            onLike={onLike}
                            onBump={onBump}
                            onBookmark={onBookmark}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onNavigateToProfile={onNavigateToProfile}
                            onNavigateToHashtag={onNavigateToHashtag}
                            onNavigateToCommunity={onNavigateToCommunity}
                            onShare={onShare}
                            onVouch={onVouch}
                            onMuteUser={onMuteUser}
                            onBlockUser={onBlockUser}
                            onGrantAdmin={onGrantAdmin}
                            onDeleteUser={onDeleteUser}
                            onReportPost={onReportPost}
                            animatingLikes={animatingLikes}
                            animatingBumps={animatingBumps}
                            animatingBookmarks={animatingBookmarks}
                            galleryAnimation={galleryAnimation}
                            postBlurOpacity={postBlurOpacity}
                            columns={1}
                            isModalOpen={isModalOpen}
                            onAddToCart={onAddToCart}
                            isHome={true}
                            isShakingMode={isShakingMode}
                            postcardGap={postcardGap}
                        />
                    </div>

                    {/* Right Arrow Button */}
                    <button
                        onClick={handleNext}
                        disabled={shuffledPosts.length <= 1}
                        className="p-[1.2vh] rounded-full bg-black/40 hover:bg-black/70 border border-white/10 backdrop-blur-md text-white transition-all transform hover:scale-105 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                        title="Next Post"
                        aria-label="Next Featured Post"
                    >
                        <svg className="w-[2.2vh] h-[2.2vh]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            ) : (
                <div 
                    className="w-full max-w-[650px] p-[3vh] rounded-[2rem] bg-black/20 border border-white/10 backdrop-blur-md text-center text-gray-400 text-[0.9rem]"
                    style={{ color: userTextColor }}
                >
                    No featured posts found for {filterMode === 'connections' ? 'connections' : 'public'}.
                </div>
            )}
        </div>
    );
};
